import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import ViewChatModal from "../../Modals/addChat";

interface Group {
  id: string;
  name: string;
  category: string;
  members: number;
  createdDate: string;
  status: boolean;
  reportedBy?: string;
  reportReason?: string;
  reportDate?: string;
  reportStatus?: string;
  messages?: {
    user: string;
    text: string;
    time: string;
  }[];
}

const ManageCommunityGroups: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDate, setFilterDate] = useState("");

  const [viewChatOpen, setViewChatOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  // Sample groups data
  const [groups, setGroups] = useState<Group[]>([
    {
      id: "GRP001",
      name: "Morning HIIT Warriors",
      category: "Fitness",
      members: 120,
      createdDate: "2025-07-15",
      status: true,
      reportedBy: "Aisha",
      reportReason: "Spam",
      reportDate: "2025-07-16",
      reportStatus: "Pending",
      messages: [
        { user: "Rohit", text: "Good morning team!", time: "8:00 AM" },
        { user: "Aisha", text: "Ready for HIIT 🔥", time: "8:02 AM" },
      ],
    },
    {
      id: "GRP002",
      name: "Yoga Enthusiasts",
      category: "Wellness",
      members: 80,
      createdDate: "2025-07-10",
      status: false,
      reportedBy: "Neha",
      reportReason: "Offensive",
      reportDate: "2025-07-12",
      reportStatus: "Reviewed",
      messages: [
        { user: "Neha", text: "Today's session was great!", time: "6:30 PM" },
      ],
    },
  ]);

  const handleToggleStatus = (id: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, status: !g.status } : g))
    );
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      setGroups((prev) => prev.filter((g) => g.id !== id));
    }
  };

  const filteredGroups = groups.filter((g) => {
    const matchSearch =
      g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchCategory = filterCategory ? g.category === filterCategory : true;
    const matchDate = filterDate ? g.createdDate === filterDate : true;

    return matchSearch && matchCategory && matchDate;
  });
    const [createdDate, setCreatedDate] = useState("");

  return (
    <>
      <div className="main_title">
        <h1>Manage Community Groups</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Community Groups
        </p>
      </div>

      <div className="cards">
        {/* Header */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Groups"
            />
          </div>

          <div className="right_s" style={{ display: "flex", gap: "10px" }}>
            {/* Filter by Category */}
            <div className="form">
              <div className="control_group">
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  displayEmpty
                  className="filter_select"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="Fitness">Fitness</MenuItem>
                  <MenuItem value="Wellness">Wellness</MenuItem>
                </Select>
              </div>

            {/* Filter by Date */}
              <div className="control_group w_33">
                <TextField
                  type="date"
                  fullWidth
                  value={createdDate}
                  onChange={(e) => setCreatedDate(e.target.value)}
                />
              </div>
            </div>

            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              + Add Group
            </Button>
          </div>
        </div>

        {/* Table */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Group Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Reported By</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Report Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Group Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredGroups.map((group) => (
                <TableRow
                  key={group.id}
                  style={{ opacity: group.status ? 1 : 0.6 }}
                >
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.category}</TableCell>
                  <TableCell>{group.members}</TableCell>

                  {/* Report Info */}
                  <TableCell>{group.reportedBy}</TableCell>
                  <TableCell>{group.reportReason}</TableCell>
                  <TableCell>{group.reportDate}</TableCell>
                  <TableCell>{group.reportStatus}</TableCell>

                  {/* Active / Deactive */}
                  <TableCell>
                    <Switch
                      checked={group.status}
                      onChange={() => handleToggleStatus(group.id)}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <div className="table_actions v2">
                      <Button
                        className="btn btn_primary sm"
                        onClick={() => {
                          setSelectedGroup(group);
                          setViewChatOpen(true);
                        }}
                      >
                        View Chat
                      </Button>

                      <IconButton>
                        <img
                          onClick={() => navigate("view")}
                          src="/static/images/eye_icon.svg"
                          alt="View"
                        />
                      </IconButton>

                      <IconButton
                        onClick={() => navigate("edit", { state: { group } })}
                      >
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(group.id)}>
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                        />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Chat Modal */}
      <ViewChatModal
        open={viewChatOpen}
        setOpen={setViewChatOpen}
        onClose={() => setViewChatOpen(false)}
        selectedGroup={selectedGroup}
      />
    </>
  );
};

export default ManageCommunityGroups;
